/** The chain drawn as links, and the control that checks it again.
 *
 * **Verify is a re-read, said as one.** The server recomputes the chain's hashes every time the chain
 * is asked for (`check` is on every page), so *Verify the chain* asks again and reports what came
 * back: the server's own state word, how many entries it recomputed and between which sequence
 * numbers. It does not say "the whole chain is verified" - that is not what the check covers, and the
 * range is printed so a reader sees what was and was not looked at. It is also not the anchor: whether
 * a head has been committed somewhere it cannot be rewritten is the second question on the panel
 * above, kept apart (ADR-0020).
 *
 * **The strip is checkable from the browser.** Each block's `prev_hash` is compared with the hash of
 * the block below it, in the rows already held, so the picture is a comparison the reader can see
 * and not a verdict the server asserts about itself. A link that does not hold is drawn broken, in
 * words as well as colour. Nothing here writes anything: an immutable record has no "fix" control.
 */

import { LinkBreak, LinkSimple, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import type { ChainCheck, ChainEntry, ChainPage } from "../api/ledger";
import { StatusChip } from "../components/ui/StatusChip";
import { ledgerEventWords } from "../lib/customerWords";
import { shortHash } from "../lib/format";
import styles from "./LedgerVerify.module.css";
import { CHECK } from "./ledgerWords";
import { keepSearch } from "./search";

interface Verifiable {
  refetch: () => Promise<{ data?: ChainPage }>;
  isFetching: boolean;
}

export function VerifyChain({ page }: { page: Verifiable }) {
  const [last, setLast] = useState<{ at: string; check: ChainCheck } | null>(null);
  const run = async () => {
    const result = await page.refetch();
    if (result.data) {
      const at = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      setLast({
        at: `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}:${pad(at.getUTCSeconds())} UTC`,
        check: result.data.check,
      });
    }
  };
  const state = last ? CHECK[last.check.state] : undefined;
  return (
    <div className={styles.verify}>
      <button
        type="button"
        className={styles.button}
        onClick={() => void run()}
        disabled={page.isFetching}
        data-testid="ledger-verify"
      >
        <ShieldCheck size={16} aria-hidden="true" /> {page.isFetching ? "Verifying…" : "Verify the chain"}
      </button>
      <p className={styles.result} role="status" aria-live="polite" data-testid="ledger-verified">
        {last ? (
          <>
            {/* A state this product has never heard of is said, in neutral - never left blank and
                never read as a pass. */}
            <StatusChip tone={state?.tone ?? "neutral"} size="sm">
              {state?.short ?? `Unrecognised result: ${last.check.state}`}
            </StatusChip>{" "}
            Recomputed at {last.at}:{" "}
            {last.check.checked_from_seq === null || last.check.checked_to_seq === null
              ? "no entries to recompute."
              : `${last.check.entries_checked} entries, #${last.check.checked_from_seq} to #${last.check.checked_to_seq}.`}
          </>
        ) : null}
      </p>
    </div>
  );
}

const SHOWN = 8;

/** Whether this entry links to the one before it, from the two rows. `undefined` for the oldest row
 * held: its predecessor is not on the page, so the comparison cannot be made and is not drawn. */
function linked(row: ChainEntry, older: ChainEntry | undefined): boolean | undefined {
  if (!older || older.seq !== row.seq - 1) return undefined;
  return row.prev_hash === older.hash;
}

export function ChainStrip({ rows }: { rows: readonly ChainEntry[] }) {
  const shown = rows.slice(0, SHOWN);
  if (shown.length === 0) return null;
  return (
    <div className={styles.strip} data-testid="ledger-strip">
      {/* Read out and not drawn: the strip is a row of links that explains itself, and the sentence is
          the accessible description of what it compares. The longer account of what the page check
          covers is in the "What the chain proves" drawer. */}
      <p className="visuallyHidden">
        The newest {shown.length} {shown.length === 1 ? "entry" : "entries"}, each checked against the one before it in
        the browser.
      </p>
      <ol className={styles.blocks} aria-label="The newest entries, newest first">
        {shown.map((row, index) => {
          const ok = linked(row, rows[index + 1]);
          return (
            <li key={row.seq} className={styles.block} data-link={ok === undefined ? "unknown" : ok ? "ok" : "broken"}>
              <Link
                to="/ledger/$seq"
                params={{ seq: String(row.seq) }}
                search={keepSearch}
                className={styles.entry}
                aria-label={`Ledger entry ${row.seq}, ${ledgerEventWords(row.type)}`}
              >
                <span className={styles.seq}>#{row.seq}</span>
                <span className={styles.type} title={row.type}>{ledgerEventWords(row.type)}</span>
                <span className={styles.hash}>{shortHash(row.hash).slice(0, 8)}</span>
              </Link>
              <span className={styles.join}>
                {ok === undefined ? null : ok ? (
                  <>
                    <LinkSimple size={14} weight="bold" aria-hidden="true" />
                    <span className="visuallyHidden">Links to entry {row.seq - 1}</span>
                  </>
                ) : (
                  <>
                    <LinkBreak size={14} weight="bold" aria-hidden="true" />
                    <span className={styles.brokenWord}>Does not link to entry {row.seq - 1}</span>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
